import React, { Suspense, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./utils/AuthContext";
import { SystemAdminProvider } from "./contexts/SystemAdminContext";
import ErrorBoundary from "./components/monitoring/ErrorBoundary";
import ComingSoonBanner from "./components/marketing/ComingSoonBanner";
import { NotificationProvider } from "./utils/NotificationContext";
import { ToastProvider } from "./utils/toastContext";
import { BrandingProvider } from "./utils/BrandingContext";
import { isFeatureEnabled } from "./utils/featureFlags";
import { replaceBrowserAlert } from "./utils/customAlert";

// Lazy load components for code splitting
const LoginPage = React.lazy(() => import("./pages/auth/LoginPage"));
const PatientLoginPage = React.lazy(() => import("./pages/auth/PatientLoginPage"));
const PatientRegistrationPage = React.lazy(() => import("./pages/auth/PatientRegistrationPage"));
const ClinicRegistrationPage = React.lazy(() => import("./pages/auth/ClinicRegistrationPage"));
const StaffOAuthCallbackPage = React.lazy(() => import("./pages/auth/StaffOAuthCallbackPage"));
const VerifyEmailPage = React.lazy(() => import("./pages/auth/VerifyEmailPage"));
const RegistrationSuccessPage = React.lazy(() => import("./pages/auth/RegistrationSuccessPage"));
const ForgotPasswordPage = React.lazy(() => import("./pages/auth/ForgotPasswordPage"));
const ForgotUsernamePage = React.lazy(() => import("./pages/auth/ForgotUsernamePage"));
const PatientForgotPasswordPage = React.lazy(() => import("./pages/auth/PatientForgotPasswordPage"));
const PatientForgotUsernamePage = React.lazy(() => import("./pages/auth/PatientForgotUsernamePage"));
const ResetPasswordPage = React.lazy(() => import("./pages/auth/ResetPasswordPage"));
const PatientResetPasswordPage = React.lazy(() => import("./pages/auth/PatientResetPasswordPage"));
const SubscribePage = React.lazy(() => import("./pages/SubscribePage"));
const StaffRegisterPage = React.lazy(() => import("./pages/StaffRegister"));
const AuthorizeClinicPage = React.lazy(() => import("./pages/auth/AuthorizeClinicPage"));

// Embed Components
const EmbedRoute = React.lazy(() => import("./pages/embed/EmbedRoute"));

// Dashboard Pages - Lazy loaded
const ClinicDashboard = React.lazy(() => import("./pages/dashboard/ClinicDashboard"));
const TenantDashboard = React.lazy(() => import("./pages/dashboard/TenantDashboard"));
const AppointmentsPage = React.lazy(() => import("./pages/dashboard/AppointmentsPage"));
const StaffPage = React.lazy(() => import("./pages/dashboard/StaffPage"));
const PatientsPage = React.lazy(() => import("./pages/dashboard/PatientsPage"));
const RolesPage = React.lazy(() => import("./pages/dashboard/RolesPage"));
const RemindersPage = React.lazy(() => import("./pages/dashboard/RemindersPage"));
const ClinicalBillingPage = React.lazy(() => import("./pages/dashboard/clinical-billing/ClinicalBillingPage"));
const IntegrationsPage = React.lazy(() => import("./pages/dashboard/IntegrationsPage"));
const StripeConnectReturnHandler = React.lazy(() => import("./components/integrations/StripeConnectReturnHandler"));
const PayPalConnectReturnHandler = React.lazy(() => import("./components/integrations/PayPalConnectReturnHandler"));
const StripeConnectRefreshHandler = React.lazy(() => import("./components/integrations/StripeConnectRefreshHandler"));
const GoogleCallbackPage = React.lazy(() => import("./pages/integrations/GoogleCallbackPage"));
const ZoomCallbackPage = React.lazy(() => import("./pages/integrations/ZoomCallbackPage"));
const PrivacyPage = React.lazy(() => import("./pages/PrivacyPage"));
const TermsPage = React.lazy(() => import("./pages/TermsPage"));
const SubscriptionManagementPage = React.lazy(() => import("./pages/admin/subscription-management/SubscriptionManagementPage"));
const NotificationsPage = React.lazy(() => import("./pages/dashboard/NotificationsPage"));
const ReportsPage = React.lazy(() => import("./pages/dashboard/ReportsPage"));
const ClinicSettingsPage = React.lazy(() => import("./pages/dashboard/ClinicSettingsPage"));
const CampaignsPage = React.lazy(() => import("./pages/dashboard/CampaignsPage"));
const BrandPage = React.lazy(() => import("./pages/dashboard/BrandPage"));
const PhoneNumbersPage = React.lazy(() => import("./pages/dashboard/PhoneNumbersPage"));
const MessageHistoryPage = React.lazy(() => import("./pages/dashboard/MessageHistoryPage"));
const AnalyticsPage = React.lazy(() => import("./pages/dashboard/AnalyticsPage"));
const SystemAnalyticsPage = React.lazy(() => import("./pages/dashboard/SystemAnalyticsPage"));
const TemplatesPage = React.lazy(() => import("./pages/dashboard/TemplatesPage"));
const ProviderNotesPage = React.lazy(() => import("./pages/dashboard/ProviderNotesPage"));
const SystemAdminPage = React.lazy(() => import("./pages/dashboard/SystemAdminPage"));
const DocumentationPage = React.lazy(() => import("./pages/dashboard/DocumentationPage"));
const SystemAdminLogin = React.lazy(() => import("./pages/auth/SystemAdminLogin"));
const PlatformSettingsPage = React.lazy(() => import("./pages/admin/PlatformSettingsPage"));
const FormsManagementPage = React.lazy(() => import("./pages/dashboard/FormsManagementPage"));
const CreateConsentPage = React.lazy(() => import("./pages/dashboard/CreateConsentPage"));
const EditConsentPage = React.lazy(() => import("./pages/dashboard/EditConsentPage"));
const CreateInvoicePage = React.lazy(() => import("./pages/dashboard/CreateInvoicePage"));
const AuditDashboard = React.lazy(() => import("./pages/dashboard/AuditDashboard"));
const UserSettingsPage = React.lazy(() => import("./pages/dashboard/UserSettingsPage"));
const UserFeedbackTrackingPage = React.lazy(() => import("./pages/feedback/UserFeedbackTrackingPage"));
const EHRIntegrationsPage = React.lazy(() => import("./pages/dashboard/ehr/EHRIntegrationsPage"));
const ChronicDiseaseManagementPage = React.lazy(() => import("./pages/dashboard/chronic-disease/ChronicDiseaseManagementPage"));
const ConditionsPage = React.lazy(() => import("./pages/dashboard/chronic-disease/ConditionsPage"));
const CarePlansPage = React.lazy(() => import("./pages/dashboard/chronic-disease/CarePlansPage"));
const ConditionDetailPage = React.lazy(() => import("./pages/dashboard/chronic-disease/ConditionDetailPage"));
const CarePlanDetailPage = React.lazy(() => import("./pages/dashboard/chronic-disease/CarePlanDetailPage"));
const ProtocolsPage = React.lazy(() => import("./pages/dashboard/chronic-disease/ProtocolsPage"));
const MonitoringPage = React.lazy(() => import("./pages/dashboard/chronic-disease/MonitoringPage"));
const InventoryManagementPage = React.lazy(() => import("./pages/inventory/InventoryManagement"));

// Patient Pages - Lazy loaded
const PatientDashboard = React.lazy(() => import("./pages/patient/PatientDashboard"));
const PatientProviderNotesPage = React.lazy(() => import("./pages/patient/ProviderNotesPage"));
const PatientConsentsPage = React.lazy(() => import("./pages/patient/ConsentsPage"));
const PatientBillingPage = React.lazy(() => import("./pages/patient/BillingPage"));
const MyConditionsPage = React.lazy(() => import("./pages/patient/MyConditionsPage"));
const MyCarePlanPage = React.lazy(() => import("./pages/patient/MyCarePlanPage"));
const SymptomTrackerPage = React.lazy(() => import("./pages/patient/SymptomTrackerPage"));
const HealthMetricsPage = React.lazy(() => import("./pages/patient/HealthMetricsPage"));

// Marketing Pages - Lazy loaded
const ClinicHomePage = React.lazy(() => import("./pages/marketing/ClinicHomePage"));
const AboutUsPage = React.lazy(() => import("./pages/marketing/AboutUsPage"));
const ContactUsPage = React.lazy(() => import("./pages/marketing/ContactUsPage"));
const ProductDetailsPage = React.lazy(() => import("./pages/marketing/ProductDetailsPage"));
const AIAgentsPage = React.lazy(() => import("./pages/marketing/AIAgentsPage"));
const PatientJourneyPage = React.lazy(() => import("./pages/marketing/PatientJourneyPage"));
const SpecialtiesPage = React.lazy(() => import("./pages/marketing/SpecialtiesPage"));
const CaseStudiesPage = React.lazy(() => import("./pages/marketing/CaseStudiesPage"));
const ComparisonPage = React.lazy(() => import("./pages/marketing/ComparisonPage"));

// Free Tools Pages - Lazy loaded
const FreeToolsHubPage = React.lazy(() => import("./pages/marketing/FreeToolsHubPage"));
const NoShowCalculatorPage = React.lazy(() => import("./pages/marketing/NoShowCalculatorPage"));
const AppointmentHeatmapPage = React.lazy(() => import("./pages/marketing/AppointmentHeatmapPage"));
const BillingLeakageEstimatorPage = React.lazy(() => import("./pages/marketing/BillingLeakageEstimatorPage"));
const CommunicationScriptsPage = React.lazy(() => import("./pages/marketing/CommunicationScriptsPage"));
const PracticeReadinessChecklistPage = React.lazy(() => import("./pages/marketing/PracticeReadinessChecklistPage"));
const ROICalculatorPage = React.lazy(() => import("./pages/marketing/ROICalculatorPage"));
const BlogPage = React.lazy(() => import("./pages/marketing/BlogPage"));
const BlogPostPage = React.lazy(() => import("./pages/marketing/BlogPostPage"));
const FAQPage = React.lazy(() => import("./pages/marketing/FAQPage"));
const AppointmentSchedulingPage = React.lazy(() => import("./pages/marketing/AppointmentSchedulingPage"));
const PatientPortalPage = React.lazy(() => import("./pages/marketing/PatientPortalPage"));
const MedicalBillingPage = React.lazy(() => import("./pages/marketing/MedicalBillingPage"));
const PricingPage = React.lazy(() => import("./pages/marketing/PricingPage"));
const ComparisonVsPage = React.lazy(() => import("./pages/marketing/ComparisonVsPage"));
const ForSpecialtyPage = React.lazy(() => import("./pages/marketing/ForSpecialtyPage"));
const ResourcePillarPage = React.lazy(() => import("./pages/marketing/ResourcePillarPage"));
const HIPAACompliancePage = React.lazy(() => import("./pages/marketing/HIPAACompliancePage"));
const DynamicDocumentationHub = React.lazy(() => import("./components/documentation/DynamicDocumentationHub"));
const DocumentationArticlePage = React.lazy(() => import("./components/documentation/DocumentationArticlePage"));

// Sales Rep Pages - Lazy loaded
const SalesRepRegisterPage = React.lazy(() => import("./pages/sales-rep/SalesRepRegisterPage"));
const SalesRepLoginPage = React.lazy(() => import("./pages/sales-rep/SalesRepLoginPage"));
const SalesRepContractPage = React.lazy(() => import("./pages/sales-rep/SalesRepContractPage"));
const SalesRepDashboard = React.lazy(() => import("./pages/sales-rep/SalesRepDashboard"));
const SalesRepDocumentationPage = React.lazy(() => import("./pages/sales-rep/SalesRepDocumentationPage"));

// Dashboard shell (Stripe + SidebarLayout + SubscriptionGuard in one lazy chunk)
const DashboardShell = React.lazy(() => import('./components/layout/DashboardShell'));
// Stripe-only wrapper for pages outside DashboardShell that need payments
const LazyStripeWrapper = React.lazy(() => import('./components/providers/LazyStripeWrapper'));

// Non-critical UI components – loaded after main content renders
const SessionWarning = React.lazy(() => import('./components/ui/SessionWarning'));
const CookieConsent = React.lazy(() => import('./components/ui/CookieConsent'));
const FormBuilderPage = React.lazy(() => import('./pages/forms/FormBuilderPage'));
const WorkflowManagementPage = React.lazy(() => import('./pages/workflow/WorkflowManagementPage'));
const FormsPage = React.lazy(() => import('./pages/forms/FormsPage'));

/**
 * Loading spinner component for async operations
 * @returns {JSX.Element} The loading spinner component
 */
const LoadingSpinner = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
    </div>
  );


/**
 * Main App component that handles routing and authentication
 * @returns {JSX.Element} The main application component
 */
export default function App() {
  const { isAuthenticated, handleLogout, isPatientLogin, isLoading } = useAuth();
  
  // Wrapper to restrict tenant dashboard to super admins only
  const ProtectedTenantDashboard = ({ children }) => {
    const { userProfile } = useAuth();
    const isSuper = userProfile?.role?.name?.toLowerCase() === 'super_admin';
    return isSuper ? children : <Navigate to="/dashboard" replace />;
  };

  // Initialize custom alert system
  useEffect(() => {
    const restoreAlert = replaceBrowserAlert();
    
    // Cleanup function to restore original alert when component unmounts
    return restoreAlert;
  }, []);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <ErrorBoundary>
      <ToastProvider>
        <BrandingProvider>
        <NotificationProvider csrfToken="">
            <Suspense fallback={<LoadingSpinner />}>
            {/* Show coming soon banner if enabled */}
            {isFeatureEnabled('SHOW_COMING_SOON') && <ComingSoonBanner />}
            
            <Routes>
              {/* Embed routes - public access */}
              <Route path="/embed/calendar/:clinicId" element={<EmbedRoute />} />
              
              {/* Public routes */}
              {isFeatureEnabled('ENABLE_LOGIN') && (
                <Route path="/login" element={<LoginPage />} />
              )}
              <Route path="/auth/staff/oauth-callback" element={<StaffOAuthCallbackPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/forgot-username" element={<ForgotUsernamePage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              {isFeatureEnabled('ENABLE_PATIENT_LOGIN') && (
                <Route path="/patient-login" element={<PatientLoginPage />} />
              )}
              {isFeatureEnabled('ENABLE_PATIENT_LOGIN') && (
                <Route path="/patient-forgot-password" element={<PatientForgotPasswordPage />} />
              )}
              {isFeatureEnabled('ENABLE_PATIENT_LOGIN') && (
                <Route path="/patient-reset-password" element={<PatientResetPasswordPage />} />
              )}
              {isFeatureEnabled('ENABLE_PATIENT_LOGIN') && (
                <Route path="/patient-forgot-username" element={<PatientForgotUsernamePage />} />
              )}
              {isFeatureEnabled('ENABLE_REGISTRATION') && (
                <>
                  <Route path="/patient-registration" element={<PatientRegistrationPage />} />
                  <Route path="/register" element={<PatientRegistrationPage />} />
                  <Route path="/registration" element={<ClinicRegistrationPage />} />
                  <Route path="/registration-success" element={<RegistrationSuccessPage />} />                
                  <Route path="/staff-register" element={<StaffRegisterPage />} />
                  <Route path="/authorize-clinic" element={<AuthorizeClinicPage />} />
                </>
              )}
              {/* Email verification route */}
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              
              {/* Integration callback routes */}
              <Route path="/integrations/google/callback" element={<GoogleCallbackPage />} />
              <Route path="/integrations/zoom/callback" element={<ZoomCallbackPage />} />
              {/* PayPal return handler - needs to be accessible even if session expired during PayPal flow */}
              <Route path="/dashboard/integrations/paypal/return" element={<PayPalConnectReturnHandler />} />
              
              {/* Sales Rep Referral Program */}
              <Route path="/sales-rep/register" element={<SalesRepRegisterPage />} />
              <Route path="/sales-rep/login" element={<SalesRepLoginPage />} />
              <Route path="/sales-rep/contract" element={<SalesRepContractPage />} />
              <Route path="/sales-rep/dashboard" element={<SalesRepDashboard />} />
              <Route path="/sales-rep/documentation" element={<SalesRepDocumentationPage />} />
              <Route path="/sales-rep/documentation/:slug" element={<SalesRepDocumentationPage />} />

              {/* Marketing pages - always available */}
              <Route path="/product-details" element={<ProductDetailsPage />} />
              <Route path="/appointment-scheduling" element={<AppointmentSchedulingPage />} />
              <Route path="/patient-portal" element={<PatientPortalPage />} />
              <Route path="/medical-billing" element={<MedicalBillingPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/ai-agents" element={<AIAgentsPage />} />
              <Route path="/about-us" element={<AboutUsPage />} />
              <Route path="/contact-us" element={<ContactUsPage />} />
              <Route path="/patient-journey" element={<PatientJourneyPage />} />
              <Route path="/specialties" element={<SpecialtiesPage />} />
              <Route path="/for/:specialty" element={<ForSpecialtyPage />} />
              <Route path="/case-studies" element={<CaseStudiesPage />} />
              <Route path="/comparison" element={<ComparisonPage />} />
              <Route path="/vs/:competitor" element={<ComparisonVsPage />} />
              <Route path="/roi-calculator" element={<ROICalculatorPage />} />
              
              {/* Free Tools */}
              <Route path="/tools" element={<FreeToolsHubPage />} />
              <Route path="/tools/no-show-calculator" element={<NoShowCalculatorPage />} />
              <Route path="/tools/appointment-heatmap" element={<AppointmentHeatmapPage />} />
              <Route path="/tools/billing-leakage-estimator" element={<BillingLeakageEstimatorPage />} />
              <Route path="/tools/communication-scripts" element={<CommunicationScriptsPage />} />
              <Route path="/tools/practice-readiness-checklist" element={<PracticeReadinessChecklistPage />} />
              
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/resources/:slug" element={<ResourcePillarPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/privacy-policy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/terms-of-service" element={<TermsPage />} />
              <Route path="/hipaa-compliance" element={<HIPAACompliancePage />} />
              <Route path="/" element={<ClinicHomePage />} />

              {/* System Admin - standalone with its own authentication */}
              <Route path="/system-admin-login" element={
                <SystemAdminProvider>
                  <SystemAdminLogin />
                </SystemAdminProvider>
              } />
              <Route path="/system-admin" element={
                <SystemAdminProvider>
                  <SystemAdminPage />
                </SystemAdminProvider>
              } />
              <Route path="/system-admin/:tab" element={
                <SystemAdminProvider>
                  <SystemAdminPage />
                </SystemAdminProvider>
              } />
              <Route path="/system-admin/*" element={
                <SystemAdminProvider>
                  <SystemAdminPage />
                </SystemAdminProvider>
              } />

              {/* Protected dashboard routes - only if enabled */}
              {isFeatureEnabled('ENABLE_DASHBOARD') && isAuthenticated && !isPatientLogin() && (
                <>
                  <Route path="/dashboard/*" element={
                    <DashboardShell handleLogout={handleLogout}>
                        <Suspense fallback={<LoadingSpinner />}>
                          <Routes>
                            <Route path="" element={<ClinicDashboard />} />
                        {/* Tenant dashboard is restricted to super admins */}
                        <Route path="tenant_dashboard" element={
                          <ProtectedTenantDashboard>
                            <TenantDashboard />
                          </ProtectedTenantDashboard>
                        } />
                        <Route path="appointments" element={<AppointmentsPage />} />
                        <Route path="staff" element={<StaffPage />} />
                        <Route path="patients" element={<PatientsPage />} />
                        <Route path="roles" element={<RolesPage />} />
                        <Route path="reminders" element={<RemindersPage />} />
                        <Route path="clinical-billing" element={<ClinicalBillingPage />} />
                        <Route path="integrations" element={<IntegrationsPage />} />
                        <Route path="integrations/stripe/return" element={<StripeConnectReturnHandler />} />
                        <Route path="integrations/stripe/refresh" element={<StripeConnectRefreshHandler />} />
                        <Route path="ehr-integrations" element={<EHRIntegrationsPage />} />
                        <Route path="notifications" element={<NotificationsPage />} />
                        <Route path="feedback" element={<UserFeedbackTrackingPage />} />
                        <Route path="reports" element={<ReportsPage />} />
                        <Route path="settings" element={<ClinicSettingsPage />} />
                        <Route path="campaigns" element={<CampaignsPage />} />
                        <Route path="brand" element={<BrandPage />} />
                        <Route path="phone-numbers" element={<PhoneNumbersPage />} />
                        <Route path="message-history" element={<MessageHistoryPage />} />
                        <Route path="analytics" element={<AnalyticsPage />} />
                        <Route path="system-analytics" element={<SystemAnalyticsPage />} />
                        <Route path="templates" element={<TemplatesPage />} />
                        <Route path="provider-notes" element={<ProviderNotesPage />} />
                        <Route path="chronic-disease" element={<ChronicDiseaseManagementPage />} />
                        <Route path="chronic-disease/conditions" element={<ConditionsPage />} />
                        <Route path="chronic-disease/conditions/:id" element={<ConditionDetailPage />} />
                        <Route path="chronic-disease/care-plans" element={<CarePlansPage />} />
                        <Route path="chronic-disease/care-plans/:id" element={<CarePlanDetailPage />} />
                        <Route path="chronic-disease/protocols" element={<ProtocolsPage />} />
                        <Route path="chronic-disease/monitoring" element={<MonitoringPage />} />
                        <Route path="inventory" element={<InventoryManagementPage />} />
                        <Route path="forms-management" element={<FormsManagementPage />} />
                        <Route path="consents/create" element={<CreateConsentPage />} />
                        <Route path="consents/edit/:id" element={<EditConsentPage />} />
                        <Route path="invoices/create" element={<CreateInvoicePage />} />
                        <Route path="audit" element={<AuditDashboard />} />
                        <Route path="user-settings" element={<UserSettingsPage />} />
                        <Route path="documentation" element={<DocumentationPage />} />
                        <Route path="documentation/:slug" element={<DocumentationPage />} />
                        <Route path="admin/subscription-management" element={<SubscriptionManagementPage />} />
                        <Route path="admin/platform-settings" element={<PlatformSettingsPage />} />
                        <Route path="*" element={
                          <div className="p-8">
                            <h1 className="text-2xl font-bold text-red-600 mb-4">Page Not Found</h1>
                            <p className="text-gray-600">The requested dashboard page could not be found.</p>
                          </div>
                        } />
                      </Routes>
                    </Suspense>
                    </DashboardShell>
                  } />
                  <Route path="/subscribe" element={<LazyStripeWrapper><SubscribePage /></LazyStripeWrapper>} />
                </>
              )}

              {/* Protected patient routes - only if enabled */}
              {isFeatureEnabled('ENABLE_DASHBOARD') && isAuthenticated && isPatientLogin() && (
                <>
                  <Route path="/patient-dashboard" element={<PatientDashboard />} />
                  <Route path="/patient-dashboard/provider-notes" element={<PatientProviderNotesPage />} />
                  <Route path="/patient-dashboard/consents" element={<PatientConsentsPage />} />
                  <Route path="/patient-dashboard/billing" element={<PatientBillingPage />} />
                  <Route path="/patient-dashboard/my-conditions" element={<MyConditionsPage />} />
                  <Route path="/patient-dashboard/my-care-plan" element={<MyCarePlanPage />} />
                  <Route path="/patient-dashboard/symptom-tracker" element={<SymptomTrackerPage />} />
                  <Route path="/patient-dashboard/health-metrics" element={<HealthMetricsPage />} />
                </>
              )}

              {/* Forms Routes */}
              <Route path="/forms" element={<FormsPage />} />
              <Route path="/forms/builder" element={<FormBuilderPage />} />
              <Route path="/forms/builder/:id" element={<FormBuilderPage />} />
              
              {/* Workflow Routes */}
              <Route path="/workflow/:appointmentId" element={<WorkflowManagementPage />} />

              {/* Redirect logic - exclude system-admin routes */}
              <Route path="*" element={
                <Navigate 
                  to={
                    isFeatureEnabled('ENABLE_DASHBOARD') && isAuthenticated 
                      ? (isPatientLogin() ? "/patient-dashboard" : "/dashboard") 
                      : "/"
                  } 
                  replace 
                />
              } />
            </Routes>
            </Suspense>
          </NotificationProvider>
        </BrandingProvider>
      </ToastProvider>
      
      {/* Global Session Warning Component */}
      <Suspense fallback={null}>
        <SessionWarning />
      </Suspense>
      
      {/* Cookie Consent Banner */}
      <Suspense fallback={null}>
        <CookieConsent />
      </Suspense>
    </ErrorBoundary>
  );
}